// Upgrade-Karte für gesperrte Funktionen in Modals, Slideovern und auf
// Detailseiten. Ersetzt dort die statische Paywall. Die Anfrage läuft inline
// über usePricingRequest, die Karte öffnet keinen weiteren Dialog, weil
// HeadlessUI 2.1 Geschwister-Dialoge nicht sauber stapelt.
//
// Wirkt nur in enforced. Ist das Feature nicht gesperrt (legacy, preview oder
// im Paket), rendert sie nichts, der Aufrufer behält dort die heutige UX.
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../constants/track-events'
import { usePartnerContext } from '../../context'
import { useEntitlements } from '../../features/pricing/hooks'
import { FEATURES } from '../../features/pricing/plans'
import { PRICING_REQUEST_KINDS } from '../../features/pricing/pricing-slack'
import { getPricingCompareUrl } from '../../features/pricing/use-feature-gate'
import {
  PRICING_REQUEST_STATUS,
  usePricingRequest
} from '../../features/pricing/use-pricing-request'
import {
  AIVY_MEETING_URL,
  classNames,
  getAivyOsCustomerUrl
} from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import Button from '../tailwind/Button'
import { LockPill, useLockTexts } from './lock-badge'

// Titel und Text nach erfolgreicher Anfrage, je Zustand
const DONE_TEXTS = {
  [PRICING_REQUEST_STATUS.SENT]: {
    title: 'pricing.dialog.sent_title',
    text: 'pricing.dialog.sent_text'
  },
  [PRICING_REQUEST_STATUS.DUPLICATE]: {
    title: 'pricing.dialog.already_sent_title',
    text: 'pricing.dialog.already_sent_text'
  },
  [PRICING_REQUEST_STATUS.DRY_RUN]: {
    title: 'pricing.dialog.admin_dry_run_title',
    text: 'pricing.dialog.admin_dry_run_text'
  }
}

const LINK =
  'inline-flex items-center gap-x-1 whitespace-nowrap rounded text-sm font-semibold text-buttonSecondaryBlue hover:text-buttonSecondaryBlue/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'

const UpgradeInline = ({ feature, source, compact, className }) => {
  const { t, i18n } = useTranslation()
  const { gate, includedText, title } = useLockTexts(feature, { source })
  const { isAdmin, plan, mode } = useEntitlements()
  const { partner } = usePartnerContext()
  const request = usePricingRequest({
    kind: PRICING_REQUEST_KINDS.FEATURE,
    key: feature,
    source
  })
  const titleId = useId()
  const statusRef = useRef(null)
  // Fokus nur nach eigenem Klick auf den Status setzen. Startet die Karte
  // schon in duplicate, bleibt der Fokus, wo er ist.
  const requestedHereRef = useRef(false)

  const done = DONE_TEXTS[request.status] || null

  useEffect(() => {
    if (done && requestedHereRef.current) statusRef.current?.focus()
  }, [done])

  if (!gate.locked) return null

  const handleRequest = () => {
    requestedHereRef.current = true
    request.send()
  }

  const description = t(`${FEATURES[feature].i18nKey}.description`)
  const RequestButton = compact ? Button.PrimarySM : Button.PrimaryBase

  return (
    <div
      role='group'
      aria-labelledby={titleId}
      data-feature={feature}
      className={classNames(
        'relative rounded-lg bg-gray-50 ring-1 ring-inset ring-gray-200',
        compact ? 'p-3' : 'p-4',
        className
      )}
    >
      <LockPill label={includedText} wrap />
      <p
        id={titleId}
        className={classNames(
          'font-semibold text-gray-900',
          compact ? 'mt-2 text-sm' : 'mt-3 text-base'
        )}
      >
        {title}
      </p>
      {!compact && <p className='mt-1 text-sm text-gray-600'>{description}</p>}
      {!compact && plan?.id && (
        <p className='mt-1 text-xs text-gray-500'>
          {t('pricing.dialog.current_plan', {
            plan: t(`pricing.plans.${plan.id}`)
          })}
        </p>
      )}

      {done ? (
        <div
          ref={statusRef}
          tabIndex={-1}
          role='status'
          className='mt-4 flex items-start gap-x-2 rounded focus:outline-none'
        >
          <CheckCircleIcon
            aria-hidden='true'
            className='size-5 shrink-0 text-green-600'
          />
          <div className='text-sm'>
            <p className='font-semibold text-gray-900'>{t(done.title)}</p>
            <p className='mt-0.5 text-gray-600'>{t(done.text)}</p>
          </div>
        </div>
      ) : (
        <>
          <div
            className={classNames(
              'flex flex-wrap items-center gap-x-4 gap-y-2',
              compact ? 'mt-3' : 'mt-4'
            )}
          >
            <RequestButton
              text={t('pricing.dialog.request_action')}
              onClick={handleRequest}
              isLoading={request.isSending}
            />
            <a
              href={AIVY_MEETING_URL}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() =>
                trackEvent(trackEvents.PRICING_MEETING_PRESSED, {
                  view: 'inline',
                  feature,
                  source: source ?? null,
                  plan: plan?.id ?? null,
                  mode
                })
              }
              className={LINK}
            >
              {t('pricing.dialog.meeting_action')}
            </a>
            <a
              href={getPricingCompareUrl(i18n?.language)}
              target='_blank'
              rel='noopener noreferrer'
              className={LINK}
            >
              {t('pricing.lock.compare_link')}
              <ArrowTopRightOnSquareIcon
                aria-hidden='true'
                className='size-3.5'
              />
            </a>
          </div>
          {request.isError && (
            <p role='alert' className='mt-2 text-sm text-red-600'>
              {t('pricing.dialog.error_text')}
            </p>
          )}
          {!compact && !request.isError && (
            <p className='mt-2 text-xs text-gray-500'>
              {t('pricing.dialog.request_hint')}
            </p>
          )}
        </>
      )}

      <p className='sr-only' aria-live='polite'>
        {request.isSending ? t('pricing.dialog.sending_text') : ''}
      </p>

      {isAdmin && partner?.id && (
        <p className='mt-3 border-t border-gray-200 pt-3 text-xs text-gray-500'>
          {t('pricing.dialog.admin_hint')}{' '}
          <a
            href={getAivyOsCustomerUrl({ partner_id: partner.id })}
            target='_blank'
            rel='noopener noreferrer'
            className='font-semibold text-primaryBlue hover:text-primaryBlue/80'
          >
            {t('pricing.dialog.admin_os_action')}
          </a>
        </p>
      )}
    </div>
  )
}

UpgradeInline.propTypes = {
  feature: PropTypes.oneOf(Object.keys(FEATURES)).isRequired,
  source: PropTypes.string,
  compact: PropTypes.bool,
  className: PropTypes.string
}

UpgradeInline.defaultProps = {
  source: undefined,
  compact: false,
  className: undefined
}

export default UpgradeInline
